import React, {useEffect, useState} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {Box, Button, Checkbox, FormControlLabel, GlobalStyles, Typography} from '@mui/material';
import {keyframes} from '@mui/material/styles';
import GalanoRegular from '../../../assets/fonts/cpc/GalanoGrotesque-Regular.otf';
import GalanoBold from '../../../assets/fonts/cpc/GalanoGrotesque-Bold.otf';
import CpcHeader from '../components/CpcHeader';
import CpcFooter from '../components/CpcFooter';
import {
    getCpcGovUserInfoService,
    isCpcGovLoggedIn,
    saveCpcGovAttestationService,
} from '../../../services/cpcGov.service';

const ATTEST_TERMS = [
    {
        h: 'Accuracy and good faith.',
        b: 'All confirmations, proposed revisions, rationales, and uploaded materials must be complete, accurate, and submitted in good faith to the best of your knowledge.',
    },
    {
        h: 'Evidence requirement.',
        b: 'Any proposed score revision must be supported by official, publicly verifiable, or otherwise auditable evidence. Unsupported assertions may be returned without review.',
    },
    {
        h: 'Source traceability.',
        b: 'Each evidence submission must identify the issuing body, document title, publication or effective date, URL or file, and the exact page, section, article, table, or figure supporting the claim.',
    },
    {
        h: 'Authority to submit.',
        b: 'You confirm that you have the right to provide the submitted information and documents and that doing so does not breach confidentiality, privacy, intellectual-property, security, or other legal obligations.',
    },
    {
        h: 'Independent validation.',
        b: 'Submitted evidence and proposed revisions may be reviewed by the CPC Index team, designated experts, partner organizations, or other authorized reviewers for verification and quality assurance.',
    },
    {
        h: 'No automatic acceptance.',
        b: 'A proposed revision does not change the CPC Index score until it has been reviewed and formally accepted through the validation process.',
    },
    {
        h: 'Responsibility for submissions.',
        b: 'The submitting organization and authorized user remain responsible for the accuracy, legality, and authenticity of their submissions. The CPC Index team is not responsible for false, misleading, unauthorized, or incomplete information supplied by users.',
    },
    {
        h: 'Audit trail and retention.',
        b: 'User identity, submission history, review decisions, and supporting evidence may be retained to maintain an auditable record of the assessment and validation process.',
    },
    {
        h: 'Use of validated information.',
        b: 'Accepted information may be used to update the national CPC Index profile, benchmarks, methodology records, and related reports in accordance with applicable governance and publication protocols.',
    },
];

const C = {
    // navy/navyDeep/white stay literal — see the matching comment in
    // NationalOverview.page.jsx. Plain text/background roles use the
    // dedicated keys below, which flip via --cpc-* (see darkMode.css).
    navy: '#003C69',
    navyDeep: '#003D69',
    navyDark: '#002855',
    cyan: '#0096C8',
    cyanDeep: '#007DB4',
    white: '#FFFFFF',
    mist: 'var(--cpc-surface-subtle, #F5F8FB)',
    muted: 'var(--cpc-text-muted, rgba(0,60,105,.72))',
    check: '#03AF9A',
    surface: 'var(--cpc-surface, #FFFFFF)',
    text: 'var(--cpc-text, #003C69)',
};

const fontFaceStyles = {
    '@font-face': [
        {fontFamily: 'GalanoGrotesque', fontWeight: 400, src: `url(${GalanoRegular}) format("opentype")`, fontDisplay: 'swap'},
        {fontFamily: 'GalanoGrotesque', fontWeight: 700, src: `url(${GalanoBold}) format("opentype")`, fontDisplay: 'swap'},
    ],
};

const cpcRise = keyframes`
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: none; }
`;

/**
 * Accepting the terms takes the reviewer STRAIGHT to their dashboard.
 *
 * Accounts are pre-created, so the terms are the only gate before someone
 * reaches their own country's data. The User Information form is not a gate —
 * nothing on the dashboard depends on it. It is required before SUBMISSION,
 * where the accountable person has to be named, and it stays reachable at any
 * time from "Edit details" in the sidebar.
 */
const AFTER_TERMS_PATH = '/dq_pro/cpc-government-dashboard/overview/executive-summary';

const DisclaimerPage = () => {
    const [attested, setAttested] = useState(false);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    // Signed in, we ask the server whether the terms were already accepted
    // before rendering them — otherwise the page flashes up for a moment even
    // for someone who is about to be redirected past it.
    const [checking, setChecking] = useState(isCpcGovLoggedIn());
    const history = useHistory();

    useEffect(() => {
        document.title = 'User Attestation and Submission Terms';
    }, []);

    /**
     * Someone who has ALREADY accepted the current terms should not be asked
     * again on every sign-in. Re-consenting is not just friction — repeatedly
     * demanding agreement to terms already agreed to devalues the act, and it
     * would write a fresh attested_at each time, burying the date that actually
     * matters.
     *
     * `replace`, not `push`, so Back does not bounce straight into this page.
     * Only the CURRENT version counts as attested — if the wording is bumped
     * server-side, everyone is legitimately asked again.
     */
    useEffect(() => {
        if (!isCpcGovLoggedIn()) return undefined;

        let alive = true;
        getCpcGovUserInfoService()
            .then((res) => {
                if (!alive) return;
                if (res?.data?.attested) {
                    history.replace(AFTER_TERMS_PATH);
                } else {
                    setChecking(false);
                }
            })
            // Cannot tell? Show the terms. Asking once too often is a far better
            // failure than letting someone in without a recorded acceptance.
            .catch(() => { if (alive) setChecking(false); });

        return () => { alive = false; };
    }, [history]);

    /**
     * Signed in, the acceptance is PERSISTED before we move on — clause 8 of the
     * terms promises an auditable record, and a record that only exists in the
     * browser is not one. If it cannot be saved we stay on the page rather than
     * let someone into the review believing they have attested when nothing was
     * written.
     *
     * Signed out, the screen stays walkable so the flow is reviewable before
     * accounts are provisioned — there is simply no one to attribute it to.
     */
    const proceed = async () => {
        if (!attested || saving) return;

        if (!isCpcGovLoggedIn()) {
            history.push(AFTER_TERMS_PATH);
            return;
        }

        setSaving(true);
        setSaveError('');
        try {
            await saveCpcGovAttestationService();
            history.push(AFTER_TERMS_PATH);
        } catch (err) {
            setSaveError(err?.message || 'Could not record your acceptance. Please try again.');
            setSaving(false);
        }
    };

    // Redirect decision still pending — render nothing rather than flash the
    // terms at someone who has already accepted them.
    if (checking) return null;

    return (
        <Box
            className="cpc-gov-scope"
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: 'GalanoGrotesque, "Helvetica Neue", Helvetica, Arial, sans-serif',
                color: C.text,
                background: C.mist,
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
            }}
        >
            <GlobalStyles styles={fontFaceStyles}/>

            <CpcHeader
                hideSignOut
                right={(
                    <Box
                        component={Link}
                        to="/dq_pro/cpc-government-dashboard"
                        sx={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: C.cyan,
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            '&:hover': {textDecoration: 'underline', color: '#fff'},
                            '@media (max-width: 720px)': {fontSize: 13},
                        }}
                    >
                        ← Back to About
                    </Box>
                )}
            />

            <Box
                component="main"
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '56px 28px 80px',
                    animation: `${cpcRise} .45s ease both`,
                    '@media (max-width: 720px)': {padding: '36px 18px 64px'},
                }}
            >
                <Box sx={{width: '100%', maxWidth: 820}}>
                    <Typography sx={{fontSize: 13, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.cyanDeep, mb: 1.75}}>
                        Before you start this review
                    </Typography>
                    <Typography component="h1" sx={{margin: '0 0 18px', fontSize: 'clamp(28px, 4vw, 40px)', lineHeight: 1.15, fontWeight: 700, color: C.text}}>
                        User Attestation and Submission Terms
                    </Typography>
                    <Typography sx={{margin: '0 0 28px', fontSize: 18, lineHeight: 1.45, fontWeight: 600, color: C.text, '@media (max-width: 720px)': {fontSize: 16}}}>
                        By selecting “I agree and wish to proceed,” you confirm that you are authorized to participate in this national review and agree to the following terms:
                    </Typography>

                    <Box sx={{background: C.surface, border: '1px solid var(--cpc-border, rgba(0,60,105,.1))', padding: '8px 0 0', boxShadow: '0 8px 28px rgba(0,40,85,.06)'}}>
                        {ATTEST_TERMS.map((term, i) => (
                            <Box
                                key={term.h}
                                sx={{
                                    display: 'flex',
                                    gap: 1.75,
                                    alignItems: 'flex-start',
                                    padding: '18px 28px',
                                    borderBottom: '1px solid var(--cpc-border, rgba(0,60,105,.08))',
                                    '@media (max-width: 720px)': {padding: '16px 18px'},
                                }}
                            >
                                <Box
                                    component="span"
                                    aria-hidden
                                    sx={{
                                        flex: 'none',
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        background: C.navy,
                                        color: C.white,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 13,
                                        fontWeight: 700,
                                        marginTop: '1px',
                                    }}
                                >
                                    {i + 1}
                                </Box>
                                <Typography
                                    sx={{
                                        fontSize: 15,
                                        lineHeight: 1.55,
                                        color: C.text,
                                        '& strong': {fontWeight: 700, color: C.text},
                                    }}
                                >
                                    <strong>{term.h}</strong> {term.b}
                                </Typography>
                            </Box>
                        ))}

                        <FormControlLabel
                            sx={{
                                margin: '22px 28px 0',
                                padding: '16px 18px',
                                background: attested ? 'rgba(3,175,154,.08)' : C.mist,
                                border: '1px solid',
                                borderColor: attested ? C.check : 'var(--cpc-border, rgba(0,60,105,.12))',
                                alignItems: 'flex-start',
                                transition: 'border-color .2s ease, background .2s ease',
                                '@media (max-width: 720px)': {margin: '18px 18px 0'},
                            }}
                            control={
                                <Checkbox
                                    checked={attested}
                                    onChange={(e) => setAttested(e.target.checked)}
                                    sx={{mt: '-9px', color: C.cyanDeep, '&.Mui-checked': {color: C.cyanDeep}}}
                                />
                            }
                            label={
                                <Typography sx={{fontSize: 14.5, lineHeight: 1.5, fontWeight: 600, color: C.text}}>
                                    I have read and agree to the User Attestation and Submission Terms above, and confirm I am authorized to participate in this national review.
                                </Typography>
                            }
                        />

                        <Box sx={{padding: '20px 28px 28px', '@media (max-width: 720px)': {padding: '18px'}}}>
                            {saveError && (
                                <Typography
                                    role="alert"
                                    sx={{fontSize: 14, fontWeight: 600, color: '#b3261e', mb: 1.5}}
                                >
                                    {saveError}
                                </Typography>
                            )}
                            <Button
                                type="button"
                                disabled={!attested || saving}
                                onClick={proceed}
                                sx={{
                                    width: '100%',
                                    border: '2px solid',
                                    borderColor: attested ? C.navy : '#c5d0da',
                                    background: attested ? C.navy : '#c5d0da',
                                    color: C.white,
                                    fontFamily: 'inherit',
                                    fontSize: 16,
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    padding: '16px 22px',
                                    borderRadius: 0,
                                    textTransform: 'none',
                                    boxShadow: attested ? '0 4px 6px rgba(0,0,0,.1)' : 'none',
                                    transition: 'background .2s ease, border-color .2s ease',
                                    '&:hover': {
                                        background: attested ? C.navyDark : '#c5d0da',
                                        borderColor: attested ? C.navyDark : '#c5d0da',
                                    },
                                    '&.Mui-disabled': {
                                        color: C.white,
                                        background: '#c5d0da',
                                        borderColor: '#c5d0da',
                                    },
                                }}
                            >
                                {saving ? 'Recording your acceptance…' : 'I agree and wish to proceed →'}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Box>

            <CpcFooter/>
        </Box>
    );
};

export default DisclaimerPage;
